/**
 * FaqPage - Manage FAQ items for the judge interface.
 *
 * CRUD interface for FAQ items that judges see during the tournament.
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownIcon from '@mui/icons-material/ArrowDownward';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { faqApi } from '@/api/client';
import type { FaqItem, FaqItemCreateRequest } from '@/api/client';

export function FaqPage() {
  const queryClient = useQueryClient();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FaqItem | null>(null);
  const [formQuestion, setFormQuestion] = useState('');
  const [formAnswer, setFormAnswer] = useState('');
  const [formActive, setFormActive] = useState(true);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<FaqItem | null>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch FAQ items
  const {
    data: faqItems = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['faq-admin'],
    queryFn: faqApi.list,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: FaqItemCreateRequest) => faqApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-admin'] });
      queryClient.invalidateQueries({ queryKey: ['faq'] });
      handleCloseDialog();
      setSnackbar({ open: true, message: 'FAQ-kohta luotu!', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Virhe luotaessa FAQ-kohtaa', severity: 'error' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<FaqItemCreateRequest> }) =>
      faqApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-admin'] });
      queryClient.invalidateQueries({ queryKey: ['faq'] });
      handleCloseDialog();
      setSnackbar({ open: true, message: 'FAQ-kohta päivitetty!', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Virhe päivitettäessä FAQ-kohtaa', severity: 'error' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => faqApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-admin'] });
      queryClient.invalidateQueries({ queryKey: ['faq'] });
      setDeleteTarget(null);
      setSnackbar({ open: true, message: 'FAQ-kohta poistettu!', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Virhe poistettaessa FAQ-kohtaa', severity: 'error' });
    },
  });

  // Reorder mutation
  const reorderMutation = useMutation({
    mutationFn: (ids: number[]) => faqApi.reorder(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-admin'] });
      queryClient.invalidateQueries({ queryKey: ['faq'] });
    },
  });

  // Handlers
  const handleOpenDialog = (item?: FaqItem) => {
    if (item) {
      setEditingItem(item);
      setFormQuestion(item.question);
      setFormAnswer(item.answer);
      setFormActive(item.active);
    } else {
      setEditingItem(null);
      setFormQuestion('');
      setFormAnswer('');
      setFormActive(true);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormQuestion('');
    setFormAnswer('');
    setFormActive(true);
  };

  const handleSave = () => {
    if (!formQuestion.trim() || !formAnswer.trim()) return;

    const data: FaqItemCreateRequest = {
      question: formQuestion.trim(),
      answer: formAnswer.trim(),
      active: formActive,
    };

    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      // Set order to be at the end
      data.order = faqItems.length;
      createMutation.mutate(data);
    }
  };

  const handleToggleActive = (item: FaqItem) => {
    updateMutation.mutate({ id: item.id, data: { active: !item.active } });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newOrder = [...faqItems];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    reorderMutation.mutate(newOrder.map((item) => item.id));
  };

  const handleMoveDown = (index: number) => {
    if (index === faqItems.length - 1) return;
    const newOrder = [...faqItems];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    reorderMutation.mutate(newOrder.map((item) => item.id));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            FAQ-hallinta
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Hallinnoi tuomarin käyttöliittymässä näkyviä FAQ-kohtia
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Lisää FAQ
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Virhe ladattaessa FAQ-tietoja
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={60}>Järjestys</TableCell>
                <TableCell>Kysymys</TableCell>
                <TableCell width={100}>Aktiivinen</TableCell>
                <TableCell width={120} align="right">Toiminnot</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Ladataan...
                  </TableCell>
                </TableRow>
              ) : faqItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Ei FAQ-kohtia. Lisää ensimmäinen klikkaamalla &quot;Lisää FAQ&quot;.
                  </TableCell>
                </TableRow>
              ) : (
                faqItems.map((item, index) => (
                  <TableRow
                    key={item.id}
                    sx={{
                      opacity: item.active ? 1 : 0.5,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <IconButton
                          size="small"
                          disabled={index === 0 || reorderMutation.isPending}
                          onClick={() => handleMoveUp(index)}
                        >
                          <ArrowUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={index === faqItems.length - 1 || reorderMutation.isPending}
                          onClick={() => handleMoveDown(index)}
                        >
                          <ArrowDownIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight={500}>
                        {item.question}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 0.5,
                          whiteSpace: 'pre-line',
                          maxHeight: 60,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {item.answer}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={item.active}
                        onChange={() => handleToggleActive(item)}
                        disabled={updateMutation.isPending}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                        title="Muokkaa"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget(item)}
                        title="Poista"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'Muokkaa FAQ-kohtaa' : 'Lisää FAQ-kohta'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Kysymys"
              value={formQuestion}
              onChange={(e) => setFormQuestion(e.target.value)}
              fullWidth
              required
              placeholder="Esim. Mitä teen jos joukkue ei saavu paikalle?"
            />
            <TextField
              label="Vastaus"
              value={formAnswer}
              onChange={(e) => setFormAnswer(e.target.value)}
              fullWidth
              required
              multiline
              rows={6}
              placeholder="Kirjoita vastaus tähän..."
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={formActive}
                onChange={(e) => setFormActive(e.target.checked)}
              />
              <Typography>Aktiivinen (näkyy tuomareille)</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Peruuta</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              !formQuestion.trim() ||
              !formAnswer.trim() ||
              createMutation.isPending ||
              updateMutation.isPending
            }
          >
            {createMutation.isPending || updateMutation.isPending
              ? 'Tallennetaan...'
              : editingItem
              ? 'Tallenna'
              : 'Lisää'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Poista FAQ-kohta?</DialogTitle>
        <DialogContent>
          <Typography>Haluatko varmasti poistaa tämän FAQ-kohdan?</Typography>
          {deleteTarget && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              &ldquo;{deleteTarget.question}&rdquo;
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Peruuta</Button>
          <Button
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Poistetaan...' : 'Poista'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
